import { EventSourcePolyfill } from 'event-source-polyfill';
import { useEffect, useRef } from 'react';
import { useStore } from 'react-redux';

import LogoutForceModal from '@/components/modals/LogoutForce';
import API, { removeAuthToken, setAuthToken } from '@/helpers/API';
import EventEmitter from '@/libs/EventEmitter';
import { modalManager } from '@/libs/ModalContainer';

const useAuthGuard = () => {
  const store = useStore();
  const eventSource = useRef(null);
  const tokenRef = useRef(null);
  const reconnectTimer = useRef(null);

  const connectSSE = (token) => {
    tokenRef.current = token;

    // 2: CLOSED 상태 시 정리
    if (eventSource.current?.readyState === 2) {
      eventSource.current.close();
      eventSource.current = null;
    }

    // 0: CONNECTING, 1: OPEN
    if ([0, 1].includes(eventSource.current?.readyState)) return;

    const url = `${process.env.REACT_APP_API_URL}/fo/auth/events`;

    eventSource.current = new EventSourcePolyfill(url, {
      headers: { Authorization: `Bearer ${token}` },
      heartbeatTimeout: 90 * 1000, // 90초
    });

    // 서버에서 중복 로그인 감지 시 강제 로그아웃
    eventSource.current.addEventListener('force-logout', ({ data }) => {
      disconnectSSE();
      removeAuthToken();

      // 리듀서 상태 초기화
      store.resetAll();

      EventEmitter.publish('force-logout');

      modalManager.show(LogoutForceModal, { data: JSON.parse(data) });
    });

    // 에러 발생 시 토큰 갱신 시도 후 재연결
    eventSource.current.onerror = async (error) => {
      console.warn('SSE error', error);
      disconnectSSE();

      // EventSource는 에러 본문을 못 읽어 만료 여부 구분 불가 → 일단 갱신 시도
      const refreshed = await API.post('/auth/token/refresh', { access_token: tokenRef.current });
      if (refreshed.success && refreshed.data?.access_token) {
        setAuthToken(refreshed.data.access_token);
        tokenRef.current = refreshed.data.access_token;
      }

      // 성공/실패 공통 경로 - 최소 간격을 둬 즉시 재연결 핫루프 방지
      scheduleReconnect();
    };
  };

  const scheduleReconnect = () => {
    if (reconnectTimer.current) return;

    reconnectTimer.current = setTimeout(() => {
      reconnectTimer.current = null;

      if (tokenRef.current) {
        connectSSE(tokenRef.current);
      }
    }, 5000);
  };

  const disconnectSSE = () => {
    // 로그아웃 시 타이머도 제거
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }

    if (eventSource.current) {
      eventSource.current.close();
      eventSource.current = null;
    }
  };

  // 윈도우 비활성 → 활성 / 절전 복귀 시 재연결
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        disconnectSSE();

        if (tokenRef.current) {
          connectSSE(tokenRef.current);
        }
      }
    };

    const handleOnline = () => {
      if (tokenRef.current) {
        connectSSE(tokenRef.current);
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline); // 온라인 상태 감지

    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      disconnectSSE();
    };
  }, []);

  return { connectSSE, disconnectSSE };
};

export default useAuthGuard;
